import { useEffect, useState } from "react";
import { Loader2, Trash2, CheckCircle2, Clock, Mail, Phone, Hash, RefreshCw, Trophy, Download, ArrowUpCircle, DollarSign, UserMinus, RotateCcw, ListPlus, ListMinus, ChevronDown, Ban, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

export function RegistrationsManager({ onPendingChange }) {
  const [regs, setRegs] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sortMode, setSortMode] = useState("date");
  const [eventDivisions, setEventDivisions] = useState({});

  const toggle = (name) => setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const isCompleted = (name) => completed.includes(name);

  // Status order for sorting: Pending (needs review) → Confirmed → Waitlist → Withdrew
  const statusRank = (r) => (r.refused ? 4 : r.withdrawn ? 3 : r.waitlisted ? 2 : r.confirmed ? 1 : 0);

  const sortRows = (rows) =>
    sortMode === "status"
      ? [...rows].sort((a, b) => statusRank(a) - statusRank(b) || (a.name || "").localeCompare(b.name || ""))
      : rows;

  const applyAll = (data) => {
    const next = data.registrations || [];
    const comp = data.completed || [];
    setRegs(next);
    setCompleted(comp);
    setEventDivisions(data.event_divisions || {});
    onPendingChange?.(next.filter((x) => !x.confirmed && !x.refused && !comp.includes(x.event_title || "Other")).length);
  };

  const apply = (next) => {
    setRegs(next);
    onPendingChange?.(next.filter((x) => !x.confirmed && !x.refused && !completed.includes(x.event_title || "Other")).length);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/registrations");
      applyAll(data);
    } catch {
      toast.error("Could not load registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/registrations")
      .then((r) => applyAll(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [emailModal, setEmailModal] = useState(null); // { title }
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailInfo, setEmailInfo] = useState({ count: null, configured: true });

  const openEmail = async (name) => {
    setEmailModal({ title: name });
    setEmailSubject(`${name} — update`);
    setEmailBody("");
    setEmailInfo({ count: null, configured: true });
    try {
      const { data } = await api.get("/admin/registrations/email-preview", { params: { title: name } });
      setEmailInfo({ count: data.count, configured: data.email_configured });
    } catch {
      setEmailInfo({ count: null, configured: true });
    }
  };

  const sendTournamentEmail = async (testCopy = false) => {
    if (!emailModal) return;
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error("Subject and message are both required."); return; }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/admin/registrations/email-tournament", { title: emailModal.title, subject: emailSubject.trim(), body: emailBody, test_copy: testCopy });
      if (data.test) {
        if (data.ok === false) toast.error(data.message || "Could not send the test copy.");
        else toast.success("Test copy sent to your inbox. Review it, then send to all players.");
      } else {
        toast.success(`Email sent to ${data.sent} confirmed player${data.sent === 1 ? "" : "s"}`);
        setEmailModal(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setEmailBusy(false);
    }
  };

  const toggleCompleted = async (name, mark) => {
    try {
      const { data } = await api.put("/admin/registrations/tournament-status", { title: name, completed: mark });
      setCompleted(data.completed || []);
      if (mark) setExpanded((prev) => ({ ...prev, [name]: false }));
      onPendingChange?.(regs.filter((x) => !x.confirmed && !(data.completed || []).includes(x.event_title || "Other")).length);
      toast.success(mark ? `${name} marked completed` : `${name} reopened`);
    } catch {
      toast.error("Could not update tournament status");
    }
  };

  const setConfirmed = async (r, confirmed) => {
    try {
      const { data } = await api.put(`/admin/registrations/${r.id}`, { confirmed, entry_fee: r.entry_fee || "" });
      apply(regs.map((x) => (x.id === r.id ? { ...x, confirmed, ...(data.emailed ? { last_emailed_at: new Date().toISOString() } : {}) } : x)));
      toast.success(confirmed ? (data.emailed ? "Confirmed — player emailed" : "Marked confirmed") : "Marked pending");
    } catch {
      toast.error("Update failed");
    }
  };

  const setFee = (id, val) => setRegs((rs) => rs.map((x) => (x.id === id ? { ...x, entry_fee: val } : x)));

  const saveFee = async (id, val) => {
    try {
      await api.put(`/admin/registrations/${id}`, { entry_fee: (val || "").trim() });
    } catch {
      toast.error("Could not save entry fee");
    }
  };

  const setPaid = async (r, paid) => {    try {
      const { data } = await api.put(`/admin/registrations/${r.id}`, { paid });
      apply(regs.map((x) => (x.id === r.id ? { ...x, paid, ...(data.emailed ? { last_emailed_at: new Date().toISOString() } : {}) } : x)));
      toast.success(paid ? (data.emailed ? "Marked paid — player emailed" : "Marked paid") : "Marked unpaid");
    } catch {
      toast.error("Update failed");
    }
  };

  const setWithdrawn = async (r, withdrawn) => {
    try {
      await api.put(`/admin/registrations/${r.id}`, { withdrawn });
      apply(regs.map((x) => (x.id === r.id ? { ...x, withdrawn } : x)));
      toast.success(withdrawn ? "Marked withdrawn — spot freed up" : "Registration restored");
    } catch {
      toast.error("Update failed");
    }
  };

  const setWaitlisted = async (r, waitlisted) => {
    try {
      const { data } = await api.put(`/admin/registrations/${r.id}`, { waitlisted });
      apply(regs.map((x) => (x.id === r.id ? { ...x, waitlisted, ...(data && data.emailed ? { last_emailed_at: new Date().toISOString() } : {}) } : x)));
      toast.success(waitlisted ? "Moved to waitlist" : "Removed from waitlist");
    } catch {
      toast.error("Update failed");
    }
  };

  // ── Editable action emails (Confirm / Mark Paid / Withdrew / To Waitlist / Promote / Resend) ──
  const ACTION_META = {
    confirm: { title: "Confirm registration", verb: "Confirm & send" },
    paid: { title: "Mark as paid", verb: "Mark paid & send" },
    withdrew: { title: "Withdraw registration", verb: "Withdraw & send" },
    waitlist: { title: "Move to waitlist", verb: "Move & send" },
    promote: { title: "Promote off waitlist", verb: "Promote & send" },
    refuse: { title: "Refuse registration", verb: "Refuse & send" },
    resend: { title: "Resend email", verb: "Send email" },
    payment_reminder: { title: "Send payment reminder", verb: "Send reminder" },
  };
  const [actionModal, setActionModal] = useState(null); // { reg, action }
  const [actSubject, setActSubject] = useState("");
  const [actBody, setActBody] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const [actLoading, setActLoading] = useState(false);
  const [actConfigured, setActConfigured] = useState(true);
  const [actTestCopy, setActTestCopy] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");

  const REFUSE_REASONS = [
    { value: "fargo_high", label: "Fargo too High", clause: "as your Fargo rating is above the maximum allowed for this event." },
    { value: "robustness_low", label: "Robustness too low", clause: "as your Fargo robustness is too low for us to confirm your rating for this event." },
    { value: "unconfirmed", label: "Unable to confirm FargoRate", clause: "as we were unable to confirm your FargoRate rating." },
    { value: "full", label: "Tournament is full", clause: "as this tournament has reached full capacity." },
  ];

  const buildRefuseBody = (reg, reason) => {
    const name = reg.name || "there";
    const title = reg.event_title || "this event";
    const clause = REFUSE_REASONS.find((r) => r.value === reason)?.clause || "as it doesn't meet the entry criteria for this event.";
    return (
      `Hi ${name},\n\n` +
      `Thank you for your interest in ${title}. Unfortunately we're unable to accept your registration at this time, ${clause}\n\n` +
      `If you believe this is a mistake or you'd like more information, please reply to this email and we'll be glad to help.\n\n` +
      `— Quinte BCA Pool League`
    );
  };

  const openActionEmail = async (r, action) => {
    if (!r.email) { toast.error("This registration has no email address."); return; }
    setActionModal({ reg: r, action });
    setActSubject(""); setActBody(""); setActLoading(true); setActConfigured(true); setActTestCopy(false); setRefuseReason("");
    try {
      const { data } = await api.get(`/admin/registrations/${r.id}/email-template`, { params: { action } });
      setActSubject(data.subject || "");
      setActBody(data.body || "");
      setActConfigured(data.email_configured);
    } catch {
      toast.error("Could not load the email template");
      setActionModal(null);
    } finally {
      setActLoading(false);
    }
  };

  const sendActionEmail = async () => {
    if (!actionModal) return;
    if (!actSubject.trim() || !actBody.trim()) { toast.error("Subject and message are both required."); return; }
    setActBusy(true);
    const { reg, action } = actionModal;
    try {
      const { data } = await api.post(`/admin/registrations/${reg.id}/send-action`, { action, subject: actSubject.trim(), body: actBody, test_copy: actTestCopy });
      if (data.test) {
        if (data.ok === false) toast.error(data.message || "Could not send the test copy.");
        else toast.success("Test copy sent to your inbox. Review it, then uncheck the box to send for real.");
      } else if (data.emailed === false) {
        toast.warning(data.message || "Status updated, but the email could not be sent.");
        setActionModal(null);
        load();
      } else {
        toast.success(`Email sent to ${reg.email}`);
        setActionModal(null);
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setActBusy(false);
    }
  };

  const setRefused = async (r, refused) => {
    try {
      await api.put(`/admin/registrations/${r.id}`, { refused });
      apply(regs.map((x) => (x.id === r.id ? { ...x, refused } : x)));
      toast.success(refused ? "Registration refused" : "Registration restored");
    } catch {
      toast.error("Update failed");
    }
  };

  const [editModal, setEditModal] = useState(null); // registration being edited
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", fargo: "", division: "" });
  const [editBusy, setEditBusy] = useState(false);

  const openEdit = (r) => {
    setEditForm({ name: r.name || "", email: r.email || "", phone: r.phone || "", fargo: r.fargo || "", division: r.division || "" });
    setEditModal(r);
  };

  const saveEdit = async () => {
    const name = (editForm.name || "").trim();
    const email = (editForm.email || "").trim();
    const phone = (editForm.phone || "").trim();
    const fargo = (editForm.fargo || "").trim();
    const division = editForm.division || "";
    if (!name) { toast.error("Name is required"); return; }
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setEditBusy(true);
    try {
      await api.put(`/admin/registrations/${editModal.id}`, { name, email, phone, fargo, division });
      apply(regs.map((x) => (x.id === editModal.id ? { ...x, name, email, phone, fargo, division } : x)));
      toast.success("Registration updated");
      setEditModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setEditBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}'s registration?`)) return;
    try {
      await api.delete(`/admin/registrations/${r.id}`);
      apply(regs.filter((x) => x.id !== r.id));
      toast.success("Registration deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    const rows = [["Tournament", "Name", "FargoRate", "Division", "Phone", "Email", "Entry Fee", "Status", "Waitlist", "Paid", "Withdrew", "Registered"]];
    regs.forEach((r) => rows.push([
      r.event_title || "",
      r.name || "",
      r.fargo || "",
      r.division || "",
      r.phone || "",
      r.email || "",
      r.entry_fee || "",
      r.confirmed ? "Confirmed" : "Pending",
      r.waitlisted ? "Yes" : "No",
      r.paid ? "Yes" : "No",
      r.withdrawn ? "Yes" : "No",
      r.created_at ? new Date(r.created_at).toLocaleString() : "",
    ]));
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quintebca-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p className="text-[#555555] inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>;

  const groups = regs.reduce((acc, r) => {
    const key = r.event_title || "Other";
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});
  const groupNames = Object.keys(groups);
  const activeNames = groupNames.filter((n) => !isCompleted(n));
  const completedNames = groupNames.filter((n) => isCompleted(n));
  const activeRegs = regs.filter((r) => !isCompleted(r.event_title || "Other"));

  const renderGroup = (name) => {
    const list = groups[name];
    const done = isCompleted(name);
    const pending = list.filter((r) => !r.confirmed && !r.withdrawn && !r.refused).length;
    const wl = list.filter((r) => r.waitlisted).length;
    const isOpen = !!expanded[name];
    return (
      <div key={name} className={`bg-white rounded-lg border overflow-hidden ${done ? "border-[#E5E7EB] opacity-80" : "border-[#E5E7EB]"}`} data-testid={`reg-group-${name}`}>
        <button
          type="button"
          onClick={() => toggle(name)}
          aria-expanded={isOpen}
          data-testid={`reg-group-toggle-${name}`}
          className={`w-full text-white px-4 py-3 flex items-center gap-2 text-left transition-colors ${done ? "bg-[#3f3f46] hover:bg-[#52525b]" : "bg-[#0B0B0C] hover:bg-[#1a1a1c]"}`}
        >
          <Trophy className={`h-4 w-4 shrink-0 ${done ? "text-gray-400" : "text-[#E11D2A]"}`} />
          <span className="font-heading font-bold">{name}</span>
          <span className="text-xs text-gray-300">({list.length})</span>
          {done ? (
            <span className="text-[11px] font-semibold text-gray-200 bg-white/15 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Completed</span>
          ) : (
            <>
              {pending > 0 && (
                <span className="text-[11px] font-semibold text-amber-300 bg-amber-500/15 rounded-full px-2 py-0.5">{pending} pending</span>
              )}
              {wl > 0 && (
                <span className="text-[11px] font-semibold text-blue-300 bg-blue-500/15 rounded-full px-2 py-0.5">{wl} waitlist</span>
              )}
            </>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 ml-auto shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
        <>
        <div className="flex justify-between items-center px-4 py-2 bg-[#F9FAF8] border-b border-[#E5E7EB] gap-2 flex-wrap">
          <button onClick={() => openEmail(name)} data-testid={`reg-email-${name}`} className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-3 py-1.5 font-semibold hover:bg-[#1E63C4]/10">
            <Mail className="h-4 w-4" /> Email all confirmed players
          </button>
          {done ? (
            <button onClick={() => toggleCompleted(name, false)} data-testid={`reg-reopen-${name}`} className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-3 py-1.5 font-semibold hover:bg-[#1E63C4]/10">
              <RotateCcw className="h-4 w-4" /> Reopen tournament
            </button>
          ) : (
            <button onClick={() => toggleCompleted(name, true)} data-testid={`reg-complete-${name}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] text-[#555555] rounded-md px-3 py-1.5 font-semibold hover:bg-[#F9FAF8]">
              <CheckCircle2 className="h-4 w-4" /> Mark tournament completed
            </button>
          )}
        </div>
        <ul className="divide-y divide-[#E5E7EB]" data-testid={`reg-group-list-${name}`}>
          {sortRows(list).map((r) => renderRow(r))}
        </ul>
        </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="registrations-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl">Tournament Registrations</h2>
          <p className="text-sm text-[#555555]">{activeRegs.length} total · {activeRegs.filter((r) => !r.confirmed && !r.refused).length} pending review · {activeRegs.filter((r) => r.waitlisted).length} on waitlist</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            data-testid="reg-sort-select"
            className="text-sm border border-[#E5E7EB] rounded-md px-3 py-2 bg-white hover:bg-[#F9FAF8]"
            aria-label="Sort registrations"
          >
            <option value="date">Sort: Registered order</option>
            <option value="status">Sort: Status</option>
          </select>
          <button onClick={() => setExpanded(Object.fromEntries(activeNames.map((n) => [n, true])))} data-testid="reg-expand-all" className="text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]">Expand all</button>
          <button onClick={() => setExpanded({})} data-testid="reg-collapse-all" className="text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]">Collapse all</button>
          <button onClick={exportCsv} disabled={regs.length === 0} data-testid="reg-export-csv" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8] disabled:opacity-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={load} data-testid="reg-refresh-btn" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {regs.length === 0 && <p className="text-[#555555] text-sm">No registrations yet. They&apos;ll appear here as players sign up.</p>}

      {activeNames.map((name) => renderGroup(name))}

      {completedNames.length > 0 && (
        <div className="pt-4 border-t border-[#E5E7EB]" data-testid="reg-completed-section">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-[#555]" />
            <h3 className="font-heading font-bold text-lg text-[#555]">Completed</h3>
            <span className="text-sm text-[#999]">({completedNames.length})</span>
          </div>
          <div className="space-y-6">
            {completedNames.map((name) => renderGroup(name))}
          </div>
        </div>
      )}

      {emailModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEmailModal(null)} data-testid="reg-email-modal">
          <div className="bg-white rounded-xl max-w-lg w-full my-8 p-6" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-5 w-5 text-[#1E63C4]" />
              <h3 className="font-heading font-bold text-lg">Email confirmed players</h3>
            </div>
            <p className="text-sm text-[#555555] mb-1">{emailModal.title}</p>
            <p className="text-xs text-[#999] mb-4" data-testid="reg-email-count">
              {emailInfo.count === null ? "Counting recipients…" : `${emailInfo.count} confirmed player${emailInfo.count === 1 ? "" : "s"} with an email will receive this.`}
            </p>
            {!emailInfo.configured && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">Email isn't configured on this environment — sending will work once deployed to production.</p>
            )}
            <label className="block text-xs font-semibold text-[#555] mb-1">Subject</label>
            <input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              data-testid="reg-email-subject"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            <label className="block text-xs font-semibold text-[#555] mb-1">Message</label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={7}
              placeholder={"Write your message here.\n\nLine breaks are preserved."}
              data-testid="reg-email-body"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            <div className="flex items-center justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => setEmailModal(null)} data-testid="reg-email-cancel" className="text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]">Cancel</button>
              <button onClick={() => sendTournamentEmail(true)} disabled={emailBusy} data-testid="reg-email-testcopy" className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-4 py-2 font-semibold hover:bg-[#1E63C4]/10 disabled:opacity-60">
                <Mail className="h-4 w-4" /> Send me a test copy
              </button>
              <button onClick={() => sendTournamentEmail(false)} disabled={emailBusy || !emailInfo.count} data-testid="reg-email-send" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send to all
              </button>
            </div>
          </div>
        </div>
      )}

      {actionModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !actBusy && setActionModal(null)} data-testid="reg-action-modal">
          <div className="bg-white rounded-xl max-w-lg w-full my-8 p-6" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-5 w-5 text-[#1E63C4]" />
              <h3 className="font-heading font-bold text-lg">{ACTION_META[actionModal.action]?.title || "Send email"}</h3>
            </div>
            <p className="text-sm text-[#555555]">To: <strong>{actionModal.reg.name}</strong> · {actionModal.reg.email}</p>
            <p className="text-xs text-[#999] mb-4">{actionModal.reg.event_title}</p>
            {!actConfigured && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">Email isn't configured on this environment — sending will work once deployed to production.</p>
            )}
            {actLoading ? (
              <div className="py-10 flex items-center justify-center text-[#555]"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading email…</div>
            ) : (
              <>
                {actionModal.action === "refuse" && (
                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-[#555] mb-1">Refuse reason</label>
                    <select
                      value={refuseReason}
                      onChange={(e) => { setRefuseReason(e.target.value); setActBody(buildRefuseBody(actionModal.reg, e.target.value)); }}
                      data-testid="reg-refuse-reason"
                      className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                    >
                      <option value="">General — doesn't meet entry criteria</option>
                      {REFUSE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <p className="text-xs text-[#999] mt-1">Picking a reason updates the message below — you can still edit it.</p>
                  </div>
                )}
                <label className="block text-xs font-semibold text-[#555] mb-1">Subject</label>
                <input
                  value={actSubject}
                  onChange={(e) => setActSubject(e.target.value)}
                  data-testid="reg-action-subject"
                  className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                />
                <label className="block text-xs font-semibold text-[#555] mb-1">Message (editable — line breaks are preserved)</label>
                <textarea
                  value={actBody}
                  onChange={(e) => setActBody(e.target.value)}
                  rows={12}
                  data-testid="reg-action-body"
                  className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                />
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="reg-action-testcopy-label">
                  <input type="checkbox" checked={actTestCopy} onChange={(e) => setActTestCopy(e.target.checked)} data-testid="reg-action-testcopy" className="h-4 w-4 accent-[#1E63C4]" />
                  <span className="text-[#333]">Send me a test copy first <span className="text-[#999]">(previews in your inbox — the registrant gets nothing and no status changes)</span></span>
                </label>
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button onClick={() => setActionModal(null)} data-testid="reg-action-cancel" className="text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]">Cancel</button>
                  <button onClick={sendActionEmail} disabled={actBusy} data-testid="reg-action-send" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                    {actBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} {actTestCopy ? "Send test copy to me" : (ACTION_META[actionModal.action]?.verb || "Send")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !editBusy && setEditModal(null)} data-testid="reg-edit-modal">
          <div className="bg-white rounded-xl max-w-lg w-full my-8 p-6" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Pencil className="h-5 w-5 text-[#1E63C4]" />
              <h3 className="font-heading font-bold text-lg">Edit registration</h3>
            </div>
            <p className="text-xs text-[#999] mb-4">{editModal.event_title}</p>
            <label className="block text-xs font-semibold text-[#555] mb-1">Name</label>
            <input
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="reg-edit-name"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            <label className="block text-xs font-semibold text-[#555] mb-1">Email</label>
            <input
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="reg-edit-email"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">Phone</label>
                <input
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  data-testid="reg-edit-phone"
                  className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">FargoRate</label>
                <input
                  value={editForm.fargo}
                  onChange={(e) => setEditForm((f) => ({ ...f, fargo: e.target.value }))}
                  data-testid="reg-edit-fargo"
                  className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                />
              </div>
            </div>
            {(eventDivisions[editModal.event_id] || []).length > 0 && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-[#555] mb-1">Division</label>
                <select
                  value={editForm.division}
                  onChange={(e) => setEditForm((f) => ({ ...f, division: e.target.value }))}
                  data-testid="reg-edit-division"
                  className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
                >
                  {!eventDivisions[editModal.event_id].includes(editForm.division) && <option value={editForm.division}>{editForm.division || "Not chosen"}</option>}
                  {eventDivisions[editModal.event_id].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setEditModal(null)} data-testid="reg-edit-cancel" className="text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]">Cancel</button>
              <button onClick={saveEdit} disabled={editBusy} data-testid="reg-edit-save" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {editBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderRow(r) {
    return (
      <li key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`reg-row-${r.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#111111]">{r.name}</span>
            {r.confirmed ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" /> Confirmed</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><Clock className="h-3 w-3" /> Pending</span>
            )}
            {r.waitlisted && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E63C4] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`reg-waitlist-${r.id}`}>Waitlist</span>
            )}
            {r.paid && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid={`reg-paid-badge-${r.id}`}><DollarSign className="h-3 w-3" /> Paid</span>
            )}
            {r.withdrawn && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#777] bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5" data-testid={`reg-withdrawn-badge-${r.id}`}>Withdrew</span>
            )}
            {r.refused && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`reg-refused-badge-${r.id}`}>Refused</span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-[#555555]">
            {r.fargo && <span className="inline-flex items-center gap-1"><Hash className="h-3.5 w-3.5 text-[#1E63C4]" /> Fargo {r.fargo}</span>}
            {r.division && <span className="inline-flex items-center gap-1 font-semibold text-[#1E63C4]" data-testid={`reg-division-${r.id}`}>Division {r.division}</span>}
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-[#1E63C4]"><Phone className="h-3.5 w-3.5 text-[#1E63C4]" /> {r.phone}</a>
            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[#1E63C4]"><Mail className="h-3.5 w-3.5 text-[#1E63C4]" /> {r.email}</a>
          </div>
          <p className="text-xs text-[#999999] mt-1">
            Registered {fmtDate(r.created_at)}
            {r.last_emailed_at && <span className="text-[#1E63C4]"> · Last emailed {fmtDate(r.last_emailed_at)}</span>}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <label className="text-xs font-medium text-[#555555]" htmlFor={`fee-${r.id}`}>Entry fee $</label>
            <input
              id={`fee-${r.id}`}
              type="text"
              inputMode="decimal"
              value={r.entry_fee || ""}
              onChange={(e) => setFee(r.id, e.target.value)}
              onBlur={(e) => saveFee(r.id, e.target.value)}
              placeholder="0.00"
              data-testid={`reg-fee-${r.id}`}
              className="w-24 rounded-md border border-[#E5E7EB] px-2 py-1 text-sm"
            />
            {!r.confirmed && <span className="text-xs text-[#999999]">set before confirming to add payment info to the email</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {r.waitlisted ? (
            <button onClick={() => openActionEmail(r, "promote")} data-testid={`reg-promote-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-3 py-1.5 font-semibold hover:bg-[#1E63C4]/10">
              <ArrowUpCircle className="h-4 w-4" /> Promote
            </button>
          ) : (
            <button onClick={() => openActionEmail(r, "waitlist")} data-testid={`reg-to-waitlist-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] text-[#555555] rounded-md px-3 py-1.5 font-semibold hover:bg-[#F9FAF8]">
              <ListPlus className="h-4 w-4" /> To waitlist
            </button>
          )}
          {r.waitlisted && (
            <button onClick={() => setWaitlisted(r, false)} data-testid={`reg-off-waitlist-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] text-[#555555] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">
              <ListMinus className="h-4 w-4" /> Off waitlist
            </button>
          )}
          {r.confirmed ? (
            <button onClick={() => setConfirmed(r, false)} data-testid={`reg-unconfirm-${r.id}`} className="text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">Mark pending</button>
          ) : (
            <button onClick={() => openActionEmail(r, "confirm")} data-testid={`reg-confirm-${r.id}`} className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-3 py-1.5 font-semibold hover:bg-[#174e9c]"><CheckCircle2 className="h-4 w-4" /> Confirm</button>
          )}
          <button
            onClick={() => (r.paid ? setPaid(r, false) : openActionEmail(r, "paid"))}
            data-testid={`reg-paid-${r.id}`}
            className={`inline-flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 font-semibold border ${r.paid ? "bg-green-50 border-green-300 text-green-700" : "border-[#E5E7EB] text-[#555555] hover:bg-[#F9FAF8]"}`}
          >
            <DollarSign className="h-4 w-4" /> {r.paid ? "Paid" : "Mark paid"}
          </button>
          {!r.paid && (
            <button onClick={() => openActionEmail(r, "payment_reminder")} disabled={!r.email} data-testid={`reg-payment-reminder-${r.id}`} title="Email a payment reminder (editable before sending — no status change)" className="inline-flex items-center gap-1.5 text-sm border border-amber-300 text-amber-700 rounded-md px-3 py-1.5 font-semibold hover:bg-amber-50 disabled:opacity-50">
              <DollarSign className="h-4 w-4" /> Payment reminder
            </button>
          )}
          {r.withdrawn ? (
            <button onClick={() => setWithdrawn(r, false)} data-testid={`reg-restore-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><RotateCcw className="h-4 w-4" /> Restore</button>
          ) : (
            <button onClick={() => openActionEmail(r, "withdrew")} data-testid={`reg-withdraw-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-amber-300 text-amber-700 rounded-md px-3 py-1.5 font-semibold hover:bg-amber-50"><UserMinus className="h-4 w-4" /> Withdrew</button>
          )}
          {r.refused ? (
            <button onClick={() => setRefused(r, false)} data-testid={`reg-unrefuse-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><RotateCcw className="h-4 w-4" /> Un-refuse</button>
          ) : (
            <button onClick={() => openActionEmail(r, "refuse")} data-testid={`reg-refuse-${r.id}`} className="inline-flex items-center gap-1.5 text-sm border border-red-300 text-red-700 rounded-md px-3 py-1.5 font-semibold hover:bg-red-50"><Ban className="h-4 w-4" /> Refuse</button>
          )}
          <button onClick={() => openActionEmail(r, "resend")} disabled={!r.email} data-testid={`reg-resend-${r.id}`} title="Preview & edit the email matching this registration's current status, then send" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] text-[#555555] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8] disabled:opacity-50">
            <Mail className="h-4 w-4" /> Resend email
          </button>
          <button onClick={() => openEdit(r)} data-testid={`reg-edit-${r.id}`} title="Edit submitted details" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] text-[#555555] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">
            <Pencil className="h-4 w-4" /> Edit
          </button>
          <button onClick={() => remove(r)} data-testid={`reg-delete-${r.id}`} className="text-red-500 hover:text-red-600 p-1.5" aria-label="Delete registration"><Trash2 className="h-4 w-4" /></button>
        </div>
      </li>
    );
  }
}
